import React, { useState, useRef, useImperativeHandle, forwardRef, useEffect } from "react";
import styles from "./FileUploaderModal.module.css";
import FileUploadTabs from "./FileUploadTabs";
import SingleFileUpload from "./SingleFileUpload";
import MultipleImageUpload from "./MultipleImageUpload";
import MediaLibrary from "./MediaLibrary";
import LoadingSpinner from "../../LayoutComponents/LoadingSpinner";
import { useToasts } from "../../LayoutComponents/ToastContext";
import i18n from "../../Services/i18n";
import { createPortal } from "react-dom";
import { useEditLock } from "./EditLockContext";

const FileUploaderModal = forwardRef(function FileUploaderModal(
  { callback, showMultiImageTab = true },
  ref,
) {
  const [isOpen, setIsOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("single");
  const [, setDirtyTick] = useState(0);

  const singleUploadRef = useRef();
  const multiUploadRef = useRef();
  const mediaRef = useRef();
  const portalWrapperRef = useRef(null);

  const { addToast } = useToasts();
  const { isLocked, unlock } = useEditLock();

  const notifyDirtyChange = () => {
    setDirtyTick((x) => x + 1);
  };

  const getTabRef = (tabName = activeTab) => {
    if (tabName === "single") return singleUploadRef.current;
    if (tabName === "multiple") return multiUploadRef.current;
    if (tabName === "library") return mediaRef.current;
    return null;
  };

  const isTabDirty = (tabName = activeTab) => {
    const tabRef = getTabRef(tabName);
    return tabRef?.isDirty?.() ?? false;
  };

  const isTopMostModal = () => {
    const modalRoot = document.getElementById("modal-root");
    if (!modalRoot || !portalWrapperRef.current) return false;
    return modalRoot.lastElementChild === portalWrapperRef.current;
  };

  const confirmDiscardIfDirty = async (tabName = activeTab) => {
    const dirty = isTabDirty(tabName);
    if (!dirty) return true;

    return window.confirm(i18n.t("Modal.UnsavedChangesConfirmChangeTab"));
  };

  useImperativeHandle(ref, () => ({
    open() {
      setIsOpen(true);
    },
    close() {
      handleCloseButton();
    },
  }));

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        if (!isTopMostModal()) return;
        event.preventDefault();
        handleCloseButton();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, activeTab, closing]);

  const handleCloseButton = async () => {
    if (closing || isSaving) return;

    const canClose = await confirmDiscardIfDirty();
    if (!canClose) return;

    close();
  };

  const close = () => {
    setClosing(true);
    setTimeout(() => {
      setClosing(false);
      setIsOpen(false);
      setActiveTab("single");
      unlock();
    }, 300);
  };

  const handleTabChange = async (tab) => {
    if (isLocked || isSaving) return;
    if (tab === activeTab) return;

    const canLeave = await confirmDiscardIfDirty(activeTab);
    if (!canLeave) return;

    setActiveTab(tab);
  };

  const handleSave = async () => {
    if (isSaving) return;
    setIsSaving(true);

    try {
      let result = null;

      if (activeTab === "single") {
        result = await singleUploadRef.current?.save?.();
      } else if (activeTab === "multiple") {
        result = await multiUploadRef.current?.save?.();
      } else if (activeTab === "library") {
        result = await mediaRef.current?.save?.();
      }

      if (result?.success) {
        await callback(result.thumbnail, result.id);
        close();
      } else {
        addToast(i18n.t("FileUploaderModal.UploadError"), "GENERIC", "ERROR", true);
      }
    } catch (err) {
      addToast(i18n.t("Generic.ServerError"), "BACKENDERROR", "ERROR", true);
    }

    setIsSaving(false);
  };

  const renderContent = () => {
    switch (activeTab) {
      case "single":
        return <SingleFileUpload ref={singleUploadRef} onDirtyChange={notifyDirtyChange} />;
      case "multiple":
        return <MultipleImageUpload ref={multiUploadRef} onDirtyChange={notifyDirtyChange} />;
      case "library":
        return <MediaLibrary ref={mediaRef} onDirtyChange={notifyDirtyChange} />;
      default:
        return null;
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div ref={portalWrapperRef}>
      <div className={`${styles.backdrop} ${closing ? styles.backdropClosing : ""}`} />
      <div className={`${styles.dialogWrapper} ${closing ? styles.dialogClosing : ""}`}>
        <div className={styles.dialogHeader}>
          <span className={styles.dialogTitle}>{i18n.t("FileUploaderModal.HeaderTitle")}</span>
          <button type="button" className={styles.dialogCloseButton} onClick={handleCloseButton}>
            X
          </button>
        </div>

        <FileUploadTabs
          activeTab={activeTab}
          onTabChange={handleTabChange}
          showMultiImageTab={showMultiImageTab}
          className={isLocked ? "locked" : ""}
        />

        <div className={styles.dialogContent}>{renderContent()}</div>

        <div className={styles.dialogFooter}>
          {!isLocked && isTabDirty() && (
            <button type="button" className={styles.formOkButton} onClick={handleSave}>
              {i18n.t("Modal.DefaultSaveButton")}
            </button>
          )}

          <button type="button" className={styles.formCancelButton} onClick={handleCloseButton}>
            {i18n.t("Modal.DefaultCancelButton")}
          </button>
        </div>

        {isSaving && (
          <div className={styles.dialogSavingOverlay}>
            <LoadingSpinner />
          </div>
        )}
      </div>
    </div>,
    document.getElementById("modal-root"),
  );
});

export default FileUploaderModal;
